import React, { FC, useRef, useState } from 'react';

import { useForm } from 'react-hook-form';
import { FormattedMessage, useIntl } from 'react-intl';
import { useQueryClient } from '@tanstack/react-query';

import { Alert, Box, Button, useMediaQuery, useTheme } from '@mui/material';
import Dialog from 'components/dialog';
import FlexBox from 'components/flex-box';
import NumberField from 'components/number-field';
import RadioSelectItem from 'components/radio-select-item';
import Select from 'components/select';
import Switch from 'components/switch';
import TextField from 'components/text-field';
import LoadingSpinner from 'components/loading-spinner';

import { useGet } from 'hooks/use-get';
import { usePost } from 'hooks/use-post';

import { Feed } from 'views/self-service/features/feed-catalog/types/feed.type';

import { useAccountState } from 'contexts/account/account-context';
import { SnackbarActionTypes, useSnackbarDispatch } from 'contexts/snackbar/SnackbarContext';
import { editDuplicatedCode } from 'views/self-service/utils/self-service';
import { getBrand } from '../api/get-brand';
import { putFeed } from '../api/put-feed';
import { FeedSubType } from '../types/feed-sub-type.enum';
import { FeedType } from '../types/feed-type.enum';
import { UpdateFeedInput } from '../types/update-feed-input.type';

type EditFeedProps = {
  idx: number;
  feed: Feed;
  feeds: Feed[] | undefined;
};

type FormValues = {
  id: number;
  feedBrandId: number | string;
  name: string;
  size: number | string;
  price: number | string;
  hidden: boolean;
  feedType: string;
  feedSubType: string;
  code: string | null;
};

const PAGE_NAME_ID = 'selfService.feedCatalog';

const EditFeed: FC<EditFeedProps> = ({ idx, feed, feeds }) => {
  const intl = useIntl();
  const queryClient = useQueryClient();
  const { account } = useAccountState();
  const snackbarDispatch = useSnackbarDispatch();
  const dialogRef = useRef<React.ElementRef<typeof Dialog>[]>([]);
  const [duplicatedCode, setDuplicatedCode] = useState(false);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  // #region FORMS
  const { handleSubmit, control, reset, getValues, setError, clearErrors } = useForm<FormValues>({
    defaultValues: {
      id: 0,
      feedBrandId: '',
      name: '',
      size: '',
      price: '',
      hidden: false,
      feedType: '',
      feedSubType: '',
      code: '',
    },
    shouldUnregister: true,
  });
  //#endregion FORMS

  // #region FETCH DATA
  const { data: allBrands } = useGet(['getBrand'], () => getBrand(account.company!.companyId), {
    enabled: !!account.company!.companyId,
  });
  // #endregion FETCH DATA

  // #region PUT DATA
  const updateFeed = usePost<Feed, { feedId: number; request: UpdateFeedInput }>(
    (request) => putFeed(account.company!.companyId, request.feedId, request.request),
    () => {
      snackbarDispatch({
        type: SnackbarActionTypes.SUCCESS_SNACKBAR,
        payload: { msg: intl.formatMessage({ id: 'snackbar.success.default' }) },
      });
      queryClient.invalidateQueries({
        queryKey: ['getFeed']
      });
      dialogRef.current.forEach((dialog) => dialog.setOpen(false));
    },
    (error) => {
      snackbarDispatch({
        type: SnackbarActionTypes.ERROR_SNACKBAR,
        payload: { msg: intl.formatMessage({ id: 'snackbar.errors.default' }), error: error },
      });
    }
  );
  // #endregion PUT DATA

  // #region HANDLERS
  const handleUpdateFeed = (request: FormValues) => {
    updateFeed.mutate({
      feedId: request.id,
      request: {
        ...request,
        feedBrandId: Number(request.feedBrandId),
        code: request.code?.trim() === '' ? null : request.code,
        size: Number(request.size),
        price: Number(request.price),
      } as UpdateFeedInput,
    });
  };

  const handleCodeError = (isDuplicate: boolean) => {
    setDuplicatedCode(isDuplicate);
    isDuplicate ? setError('code', {}) : clearErrors('code');
  };
  // #endregion HANDLRES

  return (
    <Box display="flex" justifyContent={{ xs: 'center', md: 'flex-end' }} alignItems="center">
      <Dialog
        onClose={() => handleCodeError(false)}
        ref={(el) => {
          if (el) return (dialogRef.current[idx] = el);
        }}
        maxWidth="sm"
        title={intl.formatMessage({ id: `${PAGE_NAME_ID}.editFeedCatalog.dialog.title` })}
        button={
          <Button
            onClick={() =>
              reset({
                id: feed.id,
                size: feed.size,
                name: feed.name,
                price: feed.price,
                hidden: feed.hidden,
                feedBrandId: feed.feedBrandId,
                feedType: feed.feedType,
                feedSubType: feed.feedSubType,
                code: feed.code,
              })
            }
            size="small"
            sx={{ width: { xs: '100%' }, marginTop: { xs: '5%' } }}>
            {intl.formatMessage({ id: `${PAGE_NAME_ID}.list.edit` })}
          </Button>
        }>
        <form onSubmit={handleSubmit(handleUpdateFeed)}>
          {updateFeed.isPending && <LoadingSpinner overlay />}
          <FlexBox>
            {duplicatedCode && (
              <Alert severity="warning">
                <FormattedMessage id={intl.formatMessage({ id: `${PAGE_NAME_ID}.editFeedCatalog.dialog.duplicatedCode` })} />
              </Alert>
            )}
            <TextField
              fullWidth
              sx={{ display: 'none' }}
              label="id"
              controller={{
                name: 'id',
                control: control,
              }}
            />
            <TextField
              dark
              fullWidth
              maxLength={50}
              controller={{
                name: 'name',
                control: control,
                rules: { required: intl.formatMessage({ id: 'form.rules.required' }) },
              }}
              label={intl.formatMessage({ id: `${PAGE_NAME_ID}.editFeedCatalog.dialog.inputName` })}
            />
            <TextField
              dark
              fullWidth
              maxLength={50}
              onBlur={() => handleCodeError(editDuplicatedCode(feeds, getValues('code') ?? '', getValues('id')))}
              controller={{
                name: 'code',
                control: control,
              }}
              label={intl.formatMessage({ id: `${PAGE_NAME_ID}.editFeedCatalog.dialog.inputCode` })}
            />
            <Select
              dark
              nested
              fullWidth
              stopPropagation
              controller={{
                name: 'feedBrandId',
                control: control,
                rules: {
                  required: intl.formatMessage({ id: 'form.rules.required' }),
                  validate: {
                    required: (v) => (parseInt(v) > 0 ? true : intl.formatMessage({ id: 'form.rules.required' })),
                  },
                },
              }}
              noResults={{
                show: !allBrands?.length,
                text: <FormattedMessage id={'general.noResults'} />,
              }}
              label={intl.formatMessage({ id: `${PAGE_NAME_ID}.editFeedCatalog.dialog.selectFeedBrand` })}>
              {allBrands?.map((brand) => (
                <RadioSelectItem key={brand.id} value={brand.id}>
                  {brand.name}
                </RadioSelectItem>
              ))}
            </Select>
            <Box display={'flex'} gap={4} flexWrap={isMobile ? 'wrap' : 'nowrap'}>
              <Select
                dark
                fullWidth
                controller={{
                  name: 'feedType',
                  control: control,
                }}
                label={intl.formatMessage({ id: `${PAGE_NAME_ID}.editFeedCatalog.dialog.selectFeedType` })}>
                {Object.values(FeedType).map((value, index) => (
                  <RadioSelectItem key={index} value={value}>
                    <FormattedMessage id={`${PAGE_NAME_ID}.types.${value}`} />
                  </RadioSelectItem>
                ))}
              </Select>

              <Select
                dark
                fullWidth
                controller={{
                  name: 'feedSubType',
                  control: control,
                }}
                label={intl.formatMessage({ id: `${PAGE_NAME_ID}.editFeedCatalog.dialog.selectFeedSubType` })}>
                {Object.values(FeedSubType).map((value, index) => (
                  <RadioSelectItem key={index} value={value}>
                    <FormattedMessage id={`${PAGE_NAME_ID}.types.${value}`} />
                  </RadioSelectItem>
                ))}
              </Select>
            </Box>
            <NumberField
              dark
              fullWidth
              controller={{
                name: 'size',
                control: control,
                rules: {
                  required: intl.formatMessage({ id: 'form.rules.required' }),
                  validate: {
                    positive: (v) => (parseInt(v) >= 0 ? true : intl.formatMessage({ id: 'form.rules.positive' })),
                  },
                },
              }}
              label={intl.formatMessage({ id: `${PAGE_NAME_ID}.editFeedCatalog.dialog.inputSize` })}
            />
            <NumberField
              dark
              fullWidth
              controller={{
                name: 'price',
                control: control,
                rules: {
                  required: intl.formatMessage({ id: 'form.rules.required' }),
                  validate: {
                    positive: (v) => (parseInt(v) >= 0 ? true : intl.formatMessage({ id: 'form.rules.positive' })),
                  },
                },
              }}
              label={intl.formatMessage({ id: `${PAGE_NAME_ID}.editFeedCatalog.dialog.inputPrice` })}
            />

            <Switch
              dark
              value={feed.hidden}
              controller={{
                name: 'hidden',
                control: control,
              }}
              label={intl.formatMessage({ id: `${PAGE_NAME_ID}.editFeedCatalog.dialog.inactive` })}
            />

            <Box display={'flex'} justifyContent={'end'}>
              <Button type="submit" disabled={duplicatedCode}>
                <FormattedMessage id={`${PAGE_NAME_ID}.editFeedCatalog.dialog.button`} />
              </Button>
            </Box>
          </FlexBox>
        </form>
      </Dialog>
    </Box>
  );
};

export default React.memo(EditFeed);
